// Design System Typography Component
import React from 'react';
import { Typography as MuiTypography, TypographyProps as MuiTypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DESIGN_TOKENS, TYPOGRAPHY_TOKENS, getTextStyle } from '../tokens';

type TextVariant = 
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'body1' | 'body2' | 'caption' | 'overline'
  | 'button' | 'link';

type TextColor = 
  | 'primary' | 'secondary' | 'tertiary' | 'disabled' | 'inverse'
  | 'success' | 'warning' | 'error' | 'info';

type TextAlign = 'left' | 'center' | 'right' | 'justify';

interface TypographyProps extends Omit<MuiTypographyProps, 'variant' | 'color'> {
  variant?: TextVariant;
  color?: TextColor;
  align?: TextAlign;
  truncate?: boolean;
  responsive?: boolean;
  children?: React.ReactNode;
}

// Styled typography with design system tokens
const StyledTypography = styled(MuiTypography, {
  shouldForwardProp: (prop) => !['variant', 'color', 'truncate', 'responsive'].includes(prop as string),
})<TypographyProps>(({ 
  variant = 'body1', 
  color = 'primary', 
  align = 'left',
  truncate = false,
  responsive = false 
}) => {
  // Get base text style from design tokens
  const baseStyle = getTextStyle(variant as keyof typeof TYPOGRAPHY_TOKENS.textStyles);
  
  // Color mapping
  const colorStyles = {
    primary: { color: DESIGN_TOKENS.colors.text.primary },
    secondary: { color: DESIGN_TOKENS.colors.text.secondary },
    tertiary: { color: DESIGN_TOKENS.colors.text.tertiary },
    disabled: { color: DESIGN_TOKENS.colors.text.disabled },
    inverse: { color: DESIGN_TOKENS.colors.text.inverse },
    success: { color: DESIGN_TOKENS.colors.status.success[600] },
    warning: { color: DESIGN_TOKENS.colors.status.warning[600] },
    error: { color: DESIGN_TOKENS.colors.status.error[600] },
    info: { color: DESIGN_TOKENS.colors.status.info[600] },
  };

  // Truncation styles
  const truncateStyles = truncate ? {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  } : {};

  // Responsive styles for headings
  const responsiveStyles = responsive && ['h1', 'h2'].includes(variant) ? {
    [`@media (max-width: 768px)`]: {
      fontSize: variant === 'h1' ? TYPOGRAPHY_TOKENS.responsive.mobile.h1.fontSize :
                variant === 'h2' ? TYPOGRAPHY_TOKENS.responsive.mobile.h2.fontSize :
                baseStyle.fontSize,
    },
  } : {};

  return {
    ...baseStyle,
    ...(colorStyles[color as keyof typeof colorStyles] || colorStyles.primary),
    textAlign: align,
    ...truncateStyles,
    ...responsiveStyles,
    margin: 0, // Reset default margins
  };
});

// Typography component
export const Typography: React.FC<TypographyProps> = ({
  children,
  variant = 'body1',
  color = 'primary',
  align = 'left',
  truncate = false,
  responsive = false,
  ...props
}) => {
  // Map our variants to MUI variants for semantic HTML
  const muiVariantMap: Record<string, any> = {
    h1: 'h1',
    h2: 'h2', 
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    body1: 'body1',
    body2: 'body2',
    caption: 'caption',
    overline: 'overline',
    button: 'button',
    link: 'body1', // Use body1 as base for links
  };

  return (
    <StyledTypography
      variant={muiVariantMap[variant] || 'body1'}
      color={color as any}
      align={align}
      truncate={truncate}
      responsive={responsive}
      {...props}
    >
      {children}
    </StyledTypography>
  );
};

// Specialized typography components for common use cases
export const Heading1: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="h1" responsive {...props} />
);

export const Heading2: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="h2" responsive {...props} />
);

export const Heading3: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="h3" {...props} />
);

export const BodyText: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="body1" {...props} />
);

export const Caption: React.FC<Omit<TypographyProps, 'variant'>> = (props) => (
  <Typography variant="caption" color="secondary" {...props} />
);

export const Link: React.FC<Omit<TypographyProps, 'variant'> & { href?: string }> = ({ href, children, ...props }) => (
  <Typography 
    variant="link" 
    color="info"
    component={href ? 'a' : 'span'}
    sx={{ 
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'none',
      }
    }}
    {...(href ? { href } : {})}
    {...props}
  >
    {children}
  </Typography>
);

export default Typography;